import { stringify } from 'query-string';

import 'isomorphic-fetch';
import {
  MAFIA_AGENT_AUTH_REQ,
  MAFIA_AT_MAIN,
  MAFIA_AUTH_REQ,
  MAFIA_CAPTCHA_ANSWER,
  MAFIA_CAPTCHA_REQ,
  MAFIA_CAPTCHA_TOKEN,
  MAFIA_EMAIL_TOKEN,
  MAFIA_RECOGNIZED_TOKEN,
  MAFIA_SESSION_ID,
  MAFIA_SESSION_REQ,
  MAFIA_SESSION_TOKEN,
  MAFIA_UBID_MAIN
} from 'constants/apis';
import timedFetch from 'middleware/timedFetch';
import { formatCreditCard, formatCreditCardExpiration } from 'helpers/MyAccountUtils';
import { inIframe } from 'helpers/CheckoutUtils';
import { ENDPOINT_OPAL_EXPLICITFITS } from 'apis/opal';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { err } from 'actions/errors';
import { setSessionCookies } from 'actions/session';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import marketplace from 'cfg/marketplace.json';

const { cart: { cartName }, loginAssistantPageId } = marketplace;
const TELL_A_FRIEND_ENDPOINT = '/email/v1/tellAFriend';
// TODO Update this once mafia slotz changes for report an error are deployed
const REPORT_AN_ERROR_ENDPOINT = '/email/v1/reportAnError';
/**
 * Given credentials, returns true if auth headers should be passed or false if
 * cookie auth should be requested.
 * @param  {Object} [credentials={}] mafia authentication credentials
 * @return {boolean}                 if auth headers should be passed
 */
export const useAuthHeaders = (credentials = {}, queryParamCredentials = {}) => !!(credentials['at-main'] || queryParamCredentials['auth_token']);

/**
 * Adds the given header to the header request object if the corresponding cookie exists.  If the cookie does not exist, no header is added to request object.
 *
 * This is necessary since Edge 14 barfs on fetch headers that are blank (See https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8546263/).
 *
 * Mutates headers parameter.
 * @param {Object} headers map of headers name to values to include in the http request
 * @param {Object} cookies map of cookies names to values available in the execution context
 * @param {String} mafiaHeaderName the header name to put the cookie value
 * @param {String} cookieName the cookie name to lookup in `cookies` and put into `headers` if it exists.
 */
function translateCookieToMafiaHeader(headers, cookies, mafiaHeaderName, cookieName) {
  if (cookies[cookieName]) {
    headers[mafiaHeaderName] = cookies[cookieName];
  }
  return headers;
}

/**
 * Adds the given header to the header request object if the corresponding query param exists.  If the param does not exist, no header is added to request object.
 *
 * See Edge 14 note in translateCookieToMafiaHeader()
 *
 * Mutates headers parameter.
 * @param {Object} headers map of headers name to values to include in the http request
 * @param {Object} queryParams map of query param names to values available in the execution context
 * @param {String} mafiaHeaderName the header name to put the query param value
 * @param {String} paramName the query param name to lookup in `queryParams` and put into `headers` if it exists.
 */
function translateQueryParamToMafiaHeader(headers, queryParams, mafiaHeaderName, paramName) {
  if (queryParams[paramName]) {
    headers[mafiaHeaderName] = queryParams[paramName];
  }
  return headers;
}

/**
 * Given headers and credentials, return a new set of headers which includes
 * the appropriate auth-related headers.
 * @param  {Object} [initHeaders={}] inital set of headers
 * @param  {Object} [credentials={}] mafia auth credentials
 * @return {Object}                  init headers with auth headers
 */
export function withAuthHeaders(initHeaders = {}, credentials = {}, queryParamCredentials = {}) {
  const headers = { ...initHeaders };
  headers[MAFIA_SESSION_REQ] = 'true';
  // headers for session-reliant mafia calls
  translateCookieToMafiaHeader(headers, credentials, MAFIA_SESSION_ID, 'session-id');
  translateCookieToMafiaHeader(headers, credentials, MAFIA_SESSION_TOKEN, 'session-token');
  translateCookieToMafiaHeader(headers, credentials, MAFIA_UBID_MAIN, 'ubid-main');
  translateCookieToMafiaHeader(headers, credentials, MAFIA_RECOGNIZED_TOKEN, 'x-main');
  translateQueryParamToMafiaHeader(headers, queryParamCredentials, MAFIA_EMAIL_TOKEN, 'auth_token');

  if (useAuthHeaders(credentials)) {
    translateCookieToMafiaHeader(headers, credentials, MAFIA_AT_MAIN, 'at-main');
  } else {
    headers[MAFIA_AUTH_REQ] = true;
  }

  // CSC agents - only supporting client side calls with this
  if (inIframe()) {
    headers[MAFIA_AGENT_AUTH_REQ] = true;
    delete headers[MAFIA_AUTH_REQ];
    delete headers[MAFIA_SESSION_REQ];
  }
  return headers;
}

function sendJsonDataWithCreds(url, data, options = {}) {
  const { credentials, queryParamCredentials, method = 'post', fetcher, additionalHeaders = {} } = options;
  return fetcher(url, fetchOpts({
    method,
    headers: { 'Content-Type': 'application/json', ...additionalHeaders },
    body: JSON.stringify(data)
  }, credentials, queryParamCredentials));
}

const cartErrorTranslator = {
  'cart.validation.exception': err.CART,
  'cannotAddMixedRetailAndEgc': 'Sorry, but e-Gift Cards may not be combined with other retail items. Please complete your purchase before adding to cart.',
  'max.cart.size.exceeded': `Oops! Your ${cartName} is too full. Please remove some items and try again.`
};

export function translateCartError(response) {
  // error responses from cart api contain a status code, otherwise it's an OK response
  if (response.statusCode) {
    return cartErrorTranslator[response.id] || 'Sorry, something went wrong!';
  }
  return null;
}
/**
 * Build a fetch options, given initial config and credentials. This will
 * populate necessary auth headers and fetch opts based on given credentials.
 * @param  {Object} [initOpts={}]    inital fetch opts
 * @param  {Object} [credentials={}] mafia credentials
 * @param  {Object} [queryParamCredentials={}] query param mafia credentials
 * @return {Object}                  fetch options
 */
export function fetchOpts(initOpts = {}, credentials = {}, queryParamCredentials = {}) {
  const headers = withAuthHeaders(initOpts.headers || {}, credentials, queryParamCredentials);
  const credOpts = useAuthHeaders(credentials, queryParamCredentials) ? {} : { credentials: 'include' };
  return { ...initOpts, ...credOpts, headers };
}

export function addClientHeaders(request) {
  const headers = {};
  // if request is present, we _should_ have all three values
  if (request) {
    const { clientReferer, clientIp, clientUserAgent } = request;
    headers['x-referer'] = clientReferer;
    headers['x-ip'] = clientIp;
    headers['x-user-agent'] = clientUserAgent;
  }
  return headers;
}

function captchaHeaders({ token, answer }) {
  return {
    [MAFIA_CAPTCHA_TOKEN]: token,
    [MAFIA_CAPTCHA_ANSWER]: answer
  };
}

export function withSession(payload, headers) {
  return { payload, session: headers };
}

export function cartData({ url }, querystring = '', credentials, fetcher = timedFetch('cart')) {
  const reqUrl = `${url}/v1/cart${querystring}`;

  // if a customer has logged in, pass the header that syncs between desktop and mobile
  const headers = ('x-main' in credentials ? { [MAFIA_RECOGNIZED_TOKEN]: credentials['x-main'] } : {});

  return fetcher(reqUrl, fetchOpts({
    method: 'get',
    headers: headers
  }, credentials));
}

export function changeCart({ url }, querystring = '', data, credentials, fetcher = timedFetch('cart')) {
  const reqUrl = `${url}/v1/cart${querystring}`;
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };

  if ('x-main' in credentials) {
    headers[MAFIA_RECOGNIZED_TOKEN] = credentials['x-main'];
  }

  return fetcher(reqUrl, fetchOpts({
    method: 'post',
    headers: headers,
    body: JSON.stringify(data)
  }, credentials));
}

export function addresses({ url }, credentials = {}, fetcher = timedFetch('getAddresses')) {
  const reqUrl = `${url}/v1/address`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function paymentTypes({ url, shippingAddressId }, credentials = {}, fetcher = timedFetch('getPaymentInstruments')) {
  let reqUrl = `${url}/v1/paymentInstruments`;

  if (shippingAddressId) {
    reqUrl += `?associatedAddressId=${shippingAddressId}`;
  }

  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function shipOptions({ url }, purchaseId, credentials = {}, fetcher = timedFetch('shipmentOptions')) {
  const reqUrl = `${url}/v1/shipmentOptions?purchaseId=${purchaseId}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function shipOptionsV2({ url }, purchaseId, credentials = {}, fetcher = timedFetch('shipmentOptions')) {
  const reqUrl = `${url}/v2/shipmentOptions?purchaseId=${purchaseId}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function getPin({ url }, credentials = {}, fetcher = timedFetch('getPin')) {
  const reqUrl = `${url}/freja-laas/pin`;
  return fetcher(reqUrl, fetchOpts({ method: 'post' }, credentials));
}

export function validatePin({ url }, pin, credentials = {}, fetcher = timedFetch('validatePin')) {
  const reqUrl = `${url}/freja-laas/whitelist?pin=${pin}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function getTrackingLabel({ url }, data, credentials = {}, fetcher = timedFetch('getTrackingLabel')) {
  const reqUrl = `${url}/freja-laas/generateLabel`;
  return fetcher(reqUrl, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  }, credentials));
}

export function logoutCustomer({ url }, credentials, fetcher = timedFetch('logoutCustomer')) {
  return fetcher(`${url}/v1/zap/logout`, fetchOpts({ method: 'get', mode: 'no-cors' }, credentials));
}

export function changeAddress(params, credentials = {}, saveInvalidAddress = true, fetcher = timedFetch('changeAddress')) {
  const { mafia: { url }, address, forceOriginal } = params;
  const reqUrl = `${url}/v1/address?saveInvalidAddress=${saveInvalidAddress}${forceOriginal ? '&forceOriginal=true' : ''}`;
  return fetcher(reqUrl, fetchOpts({
    method: address.addressId ? 'put' : 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(address)
  }, credentials));
}

export function deleteAddress({ url }, addressId, fetcher = timedFetch('deleteAddress')) {
  return fetcher(`${url}/v1/address?addressIds=${addressId}`, fetchOpts({ method: 'DELETE' }));
}

export function savePayment({ url }, params, credentials = {}, fetcher = timedFetch('savePaymentInstrument')) {
  const { instrument, addressId, purchaseId } = params;
  const reqUrl = `${url}/v1/paymentInstruments`;
  const {
    cc,
    expiration,
    name,
    fullName,
    isPrimary,
    paymentInstrumentId
  } = instrument;

  let expirationMonth;
  let expirationYear;

  if (expiration) {
    ({ expirationMonth, expirationYear } = formatCreditCardExpiration(expiration));
  } else {
    ({ expirationMonth, expirationYear } = instrument);
  }

  let payment = `fullName=${fullName || name}&expirationMonth=${expirationMonth}&expirationYear=${expirationYear}&addressId=${addressId}`;
  if (paymentInstrumentId) {
    payment += `&paymentInstrumentId=${paymentInstrumentId}`;
  } else {
    const formattedCreditCardValue = formatCreditCard(cc);
    const creditCardNumber = formattedCreditCardValue.length ? formattedCreditCardValue : cc;
    payment += `&addCreditCardNumber=${creditCardNumber}`;
  }

  if (purchaseId) {
    payment += `&purchaseId=${purchaseId}`;
  }

  // Always true since it isn't possible to set primary payment as non-primary, i.e. isPrimary=false
  if (isPrimary) {
    payment += '&isPrimary=true';
  }

  return fetcher(reqUrl, fetchOpts({
    method: paymentInstrumentId ? 'put' : 'post',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: payment
  }, credentials));
}

export function creditCard({ url }, number, paymentInstrumentId, addressId, purchaseId, fetcher = timedFetch('associatePaymentInstrument')) {
  const reqUrl = `${url}/v1/associatePaymentInstrument`;

  const formattedCreditCardValue = formatCreditCard(number);
  const creditCardNumber = formattedCreditCardValue.length ? formattedCreditCardValue : number;
  let data = `paymentInstrumentId=${paymentInstrumentId}&addCreditCardNumber=${creditCardNumber}`;

  if (addressId) {
    data += `&addressId=${addressId}`;
  }

  if (purchaseId) {
    data += `&purchaseId=${purchaseId}`;
  }

  return fetcher(reqUrl, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: data
  }));
}

export function primaryPayment({ url }, fullName, pid, credentials = {}, fetcher = timedFetch('makePrimaryPaymentInstrument')) {
  return fetcher(`${url}/v1/paymentInstruments`, fetchOpts({
    method: 'put',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `paymentInstrumentId=${pid}&isPrimary=true&fullName=${fullName}`
  }, credentials));
}

export function getCustomer({ url }, credentials = {}, fetcher = timedFetch('customerInfo')) {
  return fetcher(`${url}/v1/customerInfo`, fetchOpts({}, credentials));
}

export function getCustomerAuthentication({ url }, credentials, fetcher = timedFetch('customerAuth')) {
  // if at-main is available, use it and ubid-main via queryString, else pass it via cookies
  let queryParams = '';
  if (useAuthHeaders(credentials)) {
    queryParams = `?${stringify(({ 'at-main': credentials['at-main'], 'ubid-main': credentials['ubid-main'] }))}`;
  }
  return fetcher(`${url}/auth/v1/atvalid${queryParams}`, fetchOpts({ method: 'get' }, credentials));
}

export function getGiftCard({ url }, credentials = {}, fetcher = timedFetch('getGiftCard')) {
  const sessionId = credentials['session-id'];
  return fetcher(`${url}/v1/giftCard?sessionId=${sessionId}`, fetchOpts({}, credentials));
}

export function claimGiftCard({ url }, giftCardRedeemCode, credentials = {}, fetcher = timedFetch('getGiftCard')) {
  return fetcher(`${url}/v1/giftCard`, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      claimCode: giftCardRedeemCode,
      sessionId: credentials['session-id']
    })
  }, credentials));
}

export function getWishes({ url }, credentials = {}, fetcher = timedFetch('getWishes')) {
  const reqUrl = `${url}/v1/wishes`;
  const headers = ('x-main' in credentials ? { [MAFIA_RECOGNIZED_TOKEN]: credentials['x-main'] } : {});

  return fetcher(reqUrl, fetchOpts({
    method: 'get',
    headers
  }, credentials));
}

export function saveWish({ url }, credentials = {}, { category, text }, fetcher = timedFetch('saveWish')) {
  const reqUrl = `${url}/v1/wishes`;
  const headers = ('x-main' in credentials ? { [MAFIA_RECOGNIZED_TOKEN]: credentials['x-main'] } : {});

  return fetcher(reqUrl, fetchOpts({
    method: 'put',
    headers,
    body: JSON.stringify({ category, text })
  }, credentials));
}

// My Account API Proxy
export function getOrdersV3({ url }, { page = 1, pageSize = 10, gteDate, lteDate }, credentials = {}, fetcher = timedFetch('getOrdersV3')) {
  return fetcher(`${url}/v3/orders?pageSize=${pageSize}&pageNum=${page}${gteDate ? `&gteDate=${gteDate}` : ''}${lteDate ? `&lteDate=${lteDate}` : ''}&includeTracking=true`, fetchOpts({}, credentials));
}

// My Account API Proxy
export function getOrderV3({ url }, orderId, credentials = {}, fetcher = timedFetch('getOrderV3')) {
  return fetcher(`${url}/v3/order?orderId=${orderId}`, fetchOpts({ method: 'get' }, credentials));
}

export function getOrdersByPurchaseIdV2({ url }, pId, credentials = {}, fetcher = timedFetch('getOrdersByPurchaseIdV2')) {
  return getOrdersByPurchaseIdByVersion(url, pId, credentials, fetcher, 'v2');
}

function getOrdersByPurchaseIdByVersion(url, pId, credentials, fetcher, version) {
  return fetcher(`${url}/${version}/ordersByPurchaseId?includeTracking=true&purchaseId=${pId}`, fetchOpts({ method: 'get' }, credentials));
}

export function getPixelData({ url }, purchaseId, credentials = {}, fetcher = timedFetch('getPixel')) {
  const reqUrl = `${url}/v1/pixel?purchaseId=${purchaseId}`;
  return fetcher(reqUrl, fetchOpts({ method: 'get' }, credentials));
}

export function cancelOrder({ url }, { orderId, items }, credentials = {}, fetcher = timedFetch('cancelOrder')) {
  return fetcher(`${url}/v2/order/cancel`, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderId, items })
  }, credentials));
}

// Delete payment instrument for a customer
export function deletePaymentInstrument({ url }, paymentId, credentials = {}, fetcher = timedFetch('deletePaymentInstrument')) {
  return fetcher(`${url}/v1/paymentInstruments/${paymentId}`, fetchOpts({ method: 'DELETE' }, credentials));
}

// Update payment method on an order
export function updatePayment({ url }, { orderId, paymentInstrumentId }, credentials = {}, fetcher = timedFetch('updatePayment')) {
  return fetcher(`${url}/v1/updatePayment?orderId=${orderId}&paymentInstrumentId=${paymentInstrumentId}`, fetchOpts({ method: 'POST' }, credentials));
}

export function getEmailSubscriptions({ url }, credentials = {}, emailCredentials = {}, fetcher = timedFetch('getEmailSubscriptions')) {
  return fetcher(`${url}/email/auth/v1/subscriptions`, fetchOpts({ method: 'get' }, credentials, emailCredentials));
}

export function modifySubscriptions({ url }, subscriptions, credentials = {}, emailCredentials = {}, fetcher = timedFetch('modifySubscriptions')) {
  return fetcher(`${url}/email/auth/v1/subscriptions`,
    fetchOpts({
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(subscriptions)
    }, credentials, emailCredentials));
}

// Returns
export function getPrereturnInfo({ url }, orderId, orderItemIds, credentials = {}, fetcher = timedFetch('getPrereturnInfo')) {
  return fetcher(`${url}/v1/return/initiate?orderId=${orderId}&asins=${orderItemIds}`, fetchOpts({}, credentials));
}

export function placeReturn({ url }, reqData, credentials, fetcher = timedFetch('placeReturn')) {
  const reqUrl = `${url}/v1/return/submit`;
  return fetcher(reqUrl, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reqData)
  }, credentials));
}

export function getRewardsTerms({ url }, credentials = {}, fetcher = timedFetch('getRewardsTerms')) {
  const pageName = 'rewards-terms-of-use';
  return fetcher(`${url}/v1/getPage?pageName=${pageName}`, fetchOpts({}, credentials));
}

export function returnLabelInfo({ url }, { rmaId, contractId }, credentials = {}, fetcher = timedFetch('returnLabelInfo')) {
  const labelParam = contractId ? `contractId=${contractId}` : `rmaId=${rmaId}`;
  return fetcher(`${url}/v1/return/fetch/label?${labelParam}`, fetchOpts({}, credentials));
}

export function getRewards({ url }, akitaKey, credentials = {}, fetcher = timedFetch('getRewards')) {
  const opts = fetchOpts({
    headers: { 'x-api-key': akitaKey }
  }, credentials);
  return fetcher(`${url}/akita/slotz/recognized/v1/customers?includes=benefits`, opts);
}

export function enrollRewards({ url }, akitaKey, credentials = {}, fetcher = timedFetch('signupForRewards')) {
  return fetcher(`${url}/akita/slotz/full/v1/customers/enroll`, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json', 'x-api-key': akitaKey }
  }, credentials));
}

export function getRedeemableRewards({ url }, akitaKey, credentials = {}, fetcher = timedFetch('getRedeemableRewards')) {
  return fetcher(`${url}/akita/slotz/full/v1/customers/redeem`, fetchOpts({
    headers: { 'x-api-key': akitaKey }
  }, credentials));
}

export function postRedeemableRewards({ url }, spendPoints, akitaKey, credentials = {}, fetcher = timedFetch('applyRedeemableRewards')) {
  return fetcher(`${url}/akita/slotz/full/v1/customers/redeem`, fetchOpts({
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': akitaKey
    },
    body: JSON.stringify({ 'spend_points': spendPoints })
  }, credentials));
}

export function getRewardsTransactionStatus({ url }, transactionId, akitaKey, credentials = {}, fetcher = timedFetch('getRewardsTransactionStatus')) {
  return fetcher(`${url}/akita/slotz/full/v1/customers/transactions/?transaction_type=PROMOTION&reference_transaction_id=${transactionId}`, fetchOpts({
    headers: { 'x-api-key': akitaKey }
  }, credentials));
}

export function getRewardsEstimate({ url }, akitaKey, credentials = {}, data, fetcher = timedFetch('getRewardsEstimate')) {
  return fetcher(`${url}/akita/slotz/optional/v2/estimate/orders`, fetchOpts({
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': akitaKey
    },
    body: JSON.stringify(data)
  }, credentials));
}

export function setRefundOnFirstScanPreference({ url }, akitaKey, credentials = {}, rofsPreference, fetcher = timedFetch('setRefundOnFirstScanPreference')) {
  const headers = {
    'x-api-key': akitaKey,
    'Content-Type': 'application/json'
  };

  const opts = fetchOpts({
    method: 'post',
    headers,
    body: JSON.stringify({ preference: rofsPreference })
  }, credentials);

  const reqUrl = `${url}/akita/slotz/full/v1/customers/rofs_preference`;
  return fetcher(reqUrl, opts);
}

export function getVipPointActivity({ url }, akitaKey, nextPageToken, credentials = {}, fetcher = timedFetch('getVipPointActivity')) {
  let query = { limit: 4 };
  query = nextPageToken ? stringify({ ...query, 'next_page_token': nextPageToken }) : stringify(query);

  return fetcher(`${url}/akita/slotz/full/v1/customers/activities?${query}`, fetchOpts({
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': akitaKey
    }
  }, credentials));
}

export function getEGiftCardDesigns({ url }, fetcher = timedFetch('getEGCDesigns')) {
  return fetcher(`${url}/v1/eGiftCardDesigns`);
}

export function registerNotifications({ url }, subscription, credentials = {}, fetcher = timedFetch('registerNotifications')) {
  const reqUrl = `${url}/v1/push/register`;

  return fetcher(reqUrl, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      'browserEndpoint': subscription.endpoint,
      'devicePlatform': subscription.devicePlatform, // CHROME || FIREFOX
      'publicKey': subscription.keys['p256dh'],
      'authKey': subscription.keys['auth'],
      'version': '0.1'
    })
  }, credentials));
}

export function getNotificationSubscriptions({ url }, endpoint, credentials = {}, fetcher = timedFetch('notificationSubscrptions')) {
  const reqUrl = `${url}/v1/push/subscriptions?deviceId=${endpoint}`;
  return fetcher(reqUrl, fetchOpts({
    headers: { 'Content-Type': 'application/json' }
  }, credentials));
}

export function subscribeNotifications({ url }, subscriptionName, endpoint, credentials = {}, fetcher = timedFetch('notificationSubscrptions')) {
  const reqUrl = `${url}/v1/push/subscribe`;

  return fetcher(reqUrl, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      'topic': subscriptionName,
      'deviceId': endpoint
    })
  }, credentials));
}

export function unSubscribeNotifications({ url }, subscriptionName, endpoint, credentials = {}, fetcher = timedFetch('registerNotifications')) {
  const reqUrl = `${url}/v1/push/unsubscribe?topic=${subscriptionName}&deviceId=${endpoint}`;

  return fetcher(reqUrl, fetchOpts({
    method: 'delete',
    headers: { 'Content-Type': 'application/json' }
  }, credentials));
}

export function getBrandPromo({ url }, brandId, fetcher = timedFetch('getBrandPromo')) {
  return fetcher(`${url}/v1/content?pageLayout=ZapposLegacy&slotName=primary-1&pageName=pdp-brand-promo-${brandId}`);
}

export function subscribeToStockNotification({ url }, data, credentials = {}, fetcher = timedFetch('subscribeStockNotification')) {
  const additionalHeaders = {};
  if ('x-main' in credentials) {
    // for cart OOS notify me without being authed
    additionalHeaders[MAFIA_RECOGNIZED_TOKEN] = credentials['x-main'];
  }
  return sendJsonDataWithCreds(`${url}/email/subscriptions/v1/stocks`, data, { credentials, fetcher, additionalHeaders });
}

export function subscribeToList({ url }, data, credentials = {}, fetcher = timedFetch('subscribeStockNotification')) {
  return sendJsonDataWithCreds(`${url}/email/subscriptions/v2/lists`, data, { credentials, fetcher });
}

export function subscribeToBrand({ url }, data, credentials = {}, fetcher = timedFetch('subscribeToBrand')) {
  return sendJsonDataWithCreds(`${url}/email/subscriptions/v1/brands`, data, { credentials, fetcher });
}

export function subscribeToMarketingList({ url }, data, credentials = {}, fetcher = timedFetch('subscribeToMarketingList')) {
  return sendJsonDataWithCreds(`${url}/email/auth/subscribeToMarketingList`, data, { credentials, fetcher });
}

export function postTellAFriend({ url }, data, captchaResponseData, credentials = {}, fetcher = timedFetch('tellAFriend')) {
  return sendJsonDataWithCreds(`${url}${TELL_A_FRIEND_ENDPOINT}`, data, { credentials, fetcher, additionalHeaders: captchaHeaders(captchaResponseData) });
}

function requestCaptcha(mafiaBaseUrl, path, fetcher, method = 'get', credentials = {}) {
  return fetcher(`${mafiaBaseUrl}${path}`, fetchOpts({ method, headers: { [MAFIA_CAPTCHA_REQ]: 'v1' } }, credentials));
}

export function requestTellAFriendCaptcha({ url }, credentials = {}, fetcher = timedFetch('requestCaptcha')) {
  return requestCaptcha(url, TELL_A_FRIEND_ENDPOINT, fetcher, 'post', credentials);
}

export function postReportAnError({ url }, data, captchaResponseData, credentials = {}, fetcher = timedFetch('reportAnAError')) {
  return sendJsonDataWithCreds(`${url}${REPORT_AN_ERROR_ENDPOINT}`, data, { credentials, fetcher, additionalHeaders: captchaHeaders(captchaResponseData) });
}

export function requestReportAnErrorCaptcha({ url }, credentials = {}, fetcher = timedFetch('requestCaptcha')) {
  return requestCaptcha(url, REPORT_AN_ERROR_ENDPOINT, fetcher, 'post', credentials);
}

// Called when MelodyNewsfeed component mounts
export function postNewsfeedImpression({ url }, { type, eventId, lineItemId, completed }, credentials = {}, fetcher = timedFetch('newsfeedImpression')) {
  const data = {
    [type]: [{
      lineItemId,
      eventId,
      completed
    }]
  };
  return sendJsonDataWithCreds(`${url}/cronkite/newsFeed`, data, { credentials, fetcher });
}

export function postFitSurveyReply({ url }, data, credentials = {}, fetcher = timedFetch('newsfeedFitSurveyReply')) {
  return sendJsonDataWithCreds(`${url}${ENDPOINT_OPAL_EXPLICITFITS}`, data, { credentials, fetcher });
}

export function updateFitSurveyReply({ url }, { id, deleteFit }, data, credentials = {}, fetcher = timedFetch('newsfeedUpdateFitSurveyReply')) {
  return sendJsonDataWithCreds(`${url}${ENDPOINT_OPAL_EXPLICITFITS}/${id}`, data, { credentials, fetcher, method: deleteFit ? 'delete' : 'put' });
}

export function dismissNewsfeed({ url }, { type, eventId, lineItemId }, credentials = {}, fetcher = timedFetch('dismissNewsfeed')) {
  const reqUrl = `${url}/cronkite/newsFeed?type=${type}&eventId=${eventId}&lineItemId=${lineItemId}`;
  return fetcher(reqUrl, fetchOpts({
    method: 'delete',
    headers: { 'Content-Type': 'application/json' }
  }, credentials));
}

export function cartCount({ url }, credentials = {}, fetcher = timedFetch('getCartCount')) {
  return fetcher(`${url}/v1/getCartItemsCount`, fetchOpts({}, credentials));
}

export function recommendationsSearch({ url }, credentials = {}, numberOfRecos = 25, slotName = 'zap-hp-vh', searchText = 'empty', fetcher = timedFetch('recommendationsSearch')) {
  return fetcher(`${url}/v1/recommendationsSearch?&searchText=${searchText}&slotName=${slotName}&numberOfRecos=${numberOfRecos}`, fetchOpts({}, credentials));
}

export function getExplicitFits({ url }, credentials = {}, fetcher = timedFetch('getExplicitFits')) {
  return fetcher(`${url}${ENDPOINT_OPAL_EXPLICITFITS}`, fetchOpts({}, credentials));
}

export function updateExplicitFits({ url }, fitId, data, emailAuthToken, fetcher = timedFetch('updateExplicitFits')) {
  return sendJsonDataWithCreds(`${url}${ENDPOINT_OPAL_EXPLICITFITS}/${fitId}`, data, { fetcher, method: 'put', queryParamCredentials: { auth_token: emailAuthToken } });
}

// Send beacon to Mafia.  Typically used to record the response of a customer in an e-mail survey.
// auth_token is coming from a link in the body of an e-mail to a customer.
export function sendSurveyBeaconWithEmailToken({ url }, path, method = 'get', beaconData = {}, fetcher = timedFetch('sendSurveyBeaconWithEmailToken')) {
  const { auth_token: emailAuthToken } = beaconData;
  if (method === 'get') {
    const queryParams = `?${stringify({ ...beaconData })}`;
    return fetcher(`${url}${path}${queryParams}`, fetchOpts({
      headers: { 'Content-Type': 'application/json' }
    }, undefined, { auth_token: emailAuthToken }));
  } else {
    return sendJsonDataWithCreds(`${url}${path}`, beaconData, { fetcher, method, queryParamCredentials: { auth_token: emailAuthToken } });
  }
}

export function getLandingPageInfo({ url, siteId, subsiteId }, { pageName, hydraPersonalizedCategories }, credentials = {}, request = {}, fetcher = timedFetch('getLandingPageInfo')) {
  const qs = stringify({
    siteId,
    subsiteId,
    pageName,
    'ZFC-DATA': ((pageName === 'zappos-homepage' || pageName === 'testcontentpage') && hydraPersonalizedCategories) ? 'cats=true' : undefined
  }, { sort: false }
  );
  const reqUrl = `${url}/v1/getPage?${qs}`;
  const headers = addClientHeaders(request);
  // Attatch 'geo' cookie to server-side marty calls as 'zfc-geo' header
  translateCookieToMafiaHeader(headers, credentials, 'zfc-geo', 'geo');
  return fetcher(reqUrl, fetchOpts({ headers }, credentials));
}

export function getTaxonomyBrandPageInfo({ url, siteId, subsiteId }, brandId, credentials = {}, request = {}, fetcher = timedFetch('getTaxonomyBrandPageInfo')) {
  const qs = stringify({
    pageName: 'generic-brands-template',
    brandId,
    siteId,
    subsiteId
  });
  const reqUrl = `${url}/v1/getPage?${qs}`;
  const headers = addClientHeaders(request);
  // Attatch 'geo' cookie to server-side marty calls as 'zfc-geo' header
  translateCookieToMafiaHeader(headers, credentials, 'zfc-geo', 'geo');
  return fetcher(reqUrl, fetchOpts({ headers }, credentials));
}

export function getSymphonyPreviewInfo({ url }, slot, credentials = {}, fetcher = timedFetch('getSymphonyPreviewInfo')) {
  const reqUrl = `${url}/zcs/preview?slot_center-1=${slot}`;
  return fetcher(reqUrl, fetchOpts({}, credentials));
}

export function getSymphonyPdpComponents({ url, siteId, subsiteId }, { productId, styleId }, credentials = {}, fetcher = timedFetch('getSymphonyPdpComponents')) {
  const qs = stringify({
    product: productId,
    style: styleId,
    siteId,
    subsiteId
  });
  const reqUrl = `${url}/zcs/productPage?${qs}`;
  return fetcher(reqUrl, fetchOpts({}, credentials));
}

export function getSymphonySlots({ url, siteId, subsiteId }, params = {}, credentials = {}, fetcher = timedFetch('getSymphonyPdpComponents')) {
  const qs = stringify({ siteId, subsiteId, ...params });
  const reqUrl = `${url}/zcs/getSlots?${qs}`;
  return fetcher(reqUrl, fetchOpts({}, credentials));
}

export function getSymphonySearchComponents({ url }, { term }, credentials = {}, request = {}, fetcher = timedFetch('getSymphonySearchComponents')) {
  const reqUrl = `${url}/zcs/getSlots?pageName=${term}&pageLayout=search`;
  const headers = addClientHeaders(request);
  return fetcher(reqUrl, fetchOpts({ headers }, credentials));
}

export function getJanusRecos({ url }, { params, widgets, limit = 5, credentials = {}, dispatch }, fetcher = fetch) {
  const query = stringify({
    widgets,
    limit,
    ...params
  }, { encode: false });
  return fetcher(`${url}/janus/recos/get?${query}`,
    fetchOpts({
      credentials: 'include'
    }, credentials))
    .then(processHeadersMiddleware(setSessionCookies(dispatch)))
    .then(fetchErrorMiddleware);
}

export function submitRaffle({ url }, credentials = {}, data, fetcher = timedFetch('submitRaffle')) {
  return fetcher(`${url}/email/subscriptions/zen/v1/raffle`, fetchOpts({
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(data)
  }, credentials))
    .then(fetchErrorMiddleware);
}

export function getLoginAssistantContent({ url }, fetcher = timedFetch('getLoginAssistantContent')) {
  const reqUrl = `${url}/zcs/content?pageName=${loginAssistantPageId}&slotName=primary-1&pageLayout=KrsOne`;
  return fetcher(reqUrl, fetchOpts({
    headers: { 'Content-Type': 'application/json' }
  }));
}

export async function getSurveyQuestions({ url }, surveyName, fetcher = timedFetch('getSurveyQuestions')) {
  const reqUrl = `${url}/auscult/unauth/survey/take/${surveyName}`;
  const headers = {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
  const response = await fetcher(reqUrl, { headers });
  const json = await response.json();
  return json;
}

export async function saveSurveyQuestionResponse({ url }, params, credentials, fetcher = timedFetch('saveSurveyQuestionResponse')) {
  const { csrfToken, feedback, source, surveyId, questionId } = params;
  const reqUrl = `${url}/auscult/unauth/survey/take/${surveyId}`;
  const headers = { 'Content-Type': 'application/json' };
  const submission = {
    _csrf_token: csrfToken,
    id: surveyId,
    take_survey: {
      [`q${questionId}`]: feedback,
      [`qid${questionId}`]: `${questionId}`
    },
    metadata: { source }
  };
  const opts = fetchOpts({
    method: 'post',
    headers,
    body: JSON.stringify(submission)
  }, credentials);
  const response = await fetcher(reqUrl, opts);
  const json = await response.json();
  return json;
}

// Exchanges

export function initiateExchange({ url }, orderId, orderItemIds, credentials = {}, fetcher = timedFetch('initiateExchange')) {
  return fetcher(`${url}/api/v1/exchange/initiate?orderId=${orderId}&asins=${orderItemIds}`, fetchOpts({}, credentials));
}

export function placeExchange({ url }, reqData, credentials = {}, fetcher = timedFetch('placeExchange')) {
  const reqUrl = `${url}/api/v1/exchange/submit`;
  return fetcher(reqUrl, fetchOpts({
    method: 'post',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reqData)
  }, credentials));
}
